import { observable, action, computed } from "mobx";
import CustomerRepository from "~/repository/CustomerRepository";
import User from "~/models/User";
import FormStore from "~/stores/FormStore";
import Customer from "~/models/Customer";
import Tour from "~/models/Tour";
import TourRepository from "~/repository/TourRepository";
import UserMeRepository from "~/repository/UserMeRepository";
import NestStore from "~/stores/NestStore";
import SubscriptionStore from "~/stores/Subscription/SubscriptionStore";
import CustomerStatisticStore from "~/stores/Customer/CustomerStatisticStore";

class CreateTourStore extends NestStore {
  @observable selectedAddressResult = null;

  @observable addressResults = [];

  @observable showAddressDetails = false;

  @observable markerLatLng = null;

  @observable formStore = null;

  @observable user = null;

  @observable tour = null;

  @observable selectedCustomer = null;

  @observable selectedCoCustomer = null;

  @observable isMyUrbanimmersive = false;

  customerRepository = null;

  userMeRepository = null;

  @observable subscriptionStore = null;

  @observable customerStatisticStore = null;

  @observable customerHasUrbanimmersiveSpecialPricing = false;

  constructor() {
    super();

    this.customerRepository = new CustomerRepository();
    this.formStore = new FormStore({
      shootDts: null,
      shootTimezone: null,
      shootSize: "",
    });
    this.userMeRepository = new UserMeRepository();
    this.tourRepository = new TourRepository();

    this.subscriptionStore = new SubscriptionStore();
    this.customerStatisticStore = new CustomerStatisticStore();
  }

  load() {
    if (this.isLoading) {
      return Promise.resolve();
    }

    this.isLoading = true;

    return this.userMeRepository
      .fetch()
      .then(response => {
        const { user } = response.data;

        this.user = new User(user);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  setSelectedAddressResult(selectedAddressResult) {
    this.selectedAddressResult = selectedAddressResult;

    // override the marker's position on the map
    this.markerLatLng = null;
  }

  @action
  setAddressResults(addressResults) {
    this.addressResults = addressResults;
  }

  @action
  setShowAddressDetails(showAddressDetails) {
    this.showAddressDetails = showAddressDetails;
  }

  @computed
  get addressLatLng() {
    if (this.markerLatLng) {
      return this.markerLatLng;
    }

    if (this.selectedAddressResult) {
      return this.selectedAddressResult.geometry.location;
    }

    return null;
  }

  @action
  setMarkerLatLng(markerLatLng) {
    this.markerLatLng = markerLatLng;
  }

  searchCustomer(query) {
    return this.customerRepository.fetch({ query, notAsAdminProvider: 1, limit: 10 }).then(response => {
      if (response.data.customers.length === 0) {
        return [];
      }

      // We want to separate the customers per category (Active, Deactivated)
      // Reminder: A customer becomes inactive with the Deactivate button at the provider panel
      const activeCustomers = {};
      const deactivatedCustomers = {};

      for (let i = 0; i < response.data.customers.length; i += 1) {
        const customer = new Customer(response.data.customers[i]);

        customer.title = this.getCustomerTitle(customer);

        if (!customer.deactivatedDts) {
          activeCustomers[customer.title] = customer;
        } else {
          deactivatedCustomers[customer.title] = customer;
        }
      }

      // If there are no deactivated customers, we will show the simple array, no visible separation
      if (Object.keys(deactivatedCustomers).length === 0) {
        return Object.keys(activeCustomers).map(key => activeCustomers[key]);
      }

      const results = {};

      // If there are only deactivated results, we do not want to show the active section
      if (Object.keys(activeCustomers).length > 0) {
        results.active = {
          name: "Active",
          results: Object.keys(activeCustomers).map(key => activeCustomers[key]),
        };
      }

      results.deactivated = {
        name: "Deactivated",
        results: Object.keys(deactivatedCustomers).map(key => deactivatedCustomers[key]),
      };

      // Object with keys active (optional), inactive
      return results;
    });
  }

  getCustomerTitle(customer) {
    let title = `${customer.lastName}, ${customer.firstName}`;

    if (customer.company) {
      title = `${title} (${customer.company})`;
    }

    return title;
  }

  @action
  setSelectedCustomer(selectedCustomer) {
    this.selectedCustomer = selectedCustomer;

    if (
      selectedCustomer !== null &&
      typeof selectedCustomer.hasUrbanimmersiveGroupSpecialPricing === "boolean" &&
      selectedCustomer.hasUrbanimmersiveGroupSpecialPricing
    ) {
      this.customerHasUrbanimmersiveSpecialPricing = true;
      this.subscriptionStore.searchParams.customerId = selectedCustomer.customerId;
      this.subscriptionStore.searchParams.notAsAdminProvider = 1;
      this.subscriptionStore.loadIndex();
      this.customerStatisticStore.customerId = selectedCustomer.customerId;
      this.customerStatisticStore.load({ modules: ["activeToursCount"] });
    } else {
      this.customerHasUrbanimmersiveSpecialPricing = false;
      this.subscriptionStore.searchParams.customerId = null;
      this.customerStatisticStore.customerId = null;
      this.customerStatisticStore.customerStatistic = null;
    }

    if (selectedCustomer) {
      this.formStore.setProperty("customer", this.getCustomerTitle(selectedCustomer));
    }
  }

  @action
  setSelectedCoCustomer(selectedCoCustomer) {
    this.selectedCoCustomer = selectedCoCustomer;

    if (selectedCoCustomer) {
      this.formStore.setProperty("coCustomer", this.getCustomerTitle(selectedCoCustomer));
    }
  }

  /**
   * Set an existing tour.
   * @param {Tour} tour if set, then the form will be rendered in Edit mode. Note that tour.address object is
   *                    expected to be set. Warning: This method will reset the entire formStore instance
   *                    since setting a tour means affecting the address and the customer.
   */
  @action
  setTour(tour) {
    // make sure the tour maps to an existing record.
    if (!tour.tourId) {
      throw new Error("You can only set an existing tour");
    }

    if (!tour.address) {
      throw new Error(`Expected an address for tour ${tour.tourId}`);
    }

    const customer = new Customer(tour.customer);
    this.setSelectedCustomer(customer);

    this.formStore = new FormStore({
      customer: this.getCustomerTitle(customer),
      inline: tour.address.inline,
      street: tour.address.street,
      street2: tour.address.street2,
      city: tour.address.city,
      country: tour.address.country,
      state: tour.address.state,
      zip: tour.address.zip,
      shootHasImmersive3dTour: this.isMyUrbanimmersive,
      shootDts: null,
      shootTimezone: null,
      shootSize: "",
    });

    // stub/mock a google maps address result so we can show a proper map location.
    const addressResultStub = {
      geometry: {
        location: {
          lat: parseFloat(tour.latitude),
          lng: parseFloat(tour.longitude),
        },
      },
    };

    this.setSelectedAddressResult(addressResultStub);

    this.tour = tour;
  }

  @action
  setIsMyUrbanimmersive(isMyUrbanimmersive) {
    this.isMyUrbanimmersive = isMyUrbanimmersive;
    this.formStore.setProperty("shootHasImmersive3dTour", isMyUrbanimmersive);

    return this;
  }

  getNumericLatLng() {
    let lat = 0;
    let lng = 0;

    if (this.addressLatLng) {
      lat = typeof this.addressLatLng.lat === "function" ? this.addressLatLng.lat() : this.addressLatLng.lat;
      lng = typeof this.addressLatLng.lng === "function" ? this.addressLatLng.lng() : this.addressLatLng.lng;
    }

    return {
      lat,
      lng,
    };
  }

  @action
  submit({ street, street2, city, country, state, zip, shootHasImmersive3dTour, shootDts, shootTimezone, shootSize }) {
    if (this.isSaving) {
      return Promise.resolve();
    }

    // This method should not be called if CreateTour.jsx is not valid.
    if (!this.selectedCustomer) {
      throw new Error("A customer was expected to be set");
    }

    const latLng = this.getNumericLatLng();
    const latitude = latLng.lat;
    const longitude = latLng.lng;

    // This payload is compliant with legacy api.
    const payload = {
      customerId: this.selectedCustomer.customerId,
      coCustomerId: this.selectedCoCustomer ? this.selectedCoCustomer.customerId : null,
      address: {
        street,
        street2,
        city,
        country,
        state,
        zip,
      },
      latitude,
      longitude,
      photos: null,
    };

    if (shootHasImmersive3dTour) {
      payload.shootHasImmersive3dTour = shootHasImmersive3dTour;

      if (shootDts) {
        payload.shootDts = `${shootDts}:00+00:00`;
      }

      if (shootTimezone) {
        payload.shootTimezone = shootTimezone;
      }

      if (shootSize) {
        payload.shootSize = parseInt(shootSize, 10);
      }
    }

    // This never gets set back to false because we'll redirect to another page.
    this.isSaving = true;
    this.errorMessages = {};
    this.errorMessage = "";

    let promise = null;
    let inEditMode = false;

    // If there's a tour already, then execute an update
    if (this.tour && this.tour.tourId) {
      inEditMode = true;
      promise = this.tourRepository.update(this.tour.tourId, payload);
    } else {
      promise = this.tourRepository.create(payload);
      inEditMode = false;
    }

    return promise
      .then(response => {
        // if in edit mode, we want to stop the spinner showing up. Otherwise, if in creation we want the
        // spinner to show for ever until the user is redirected to the tour edit page.
        if (inEditMode) {
          this.isSaving = false;
        }

        this.tour = new Tour(response.data.tour);

        return response;
      })
      .catch(e => {
        this.isSaving = false;
        this.setErrorMessages(e);

        throw e;
      });
  }
}

export default CreateTourStore;
